import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Slider from "react-slick";
import config from "../../../config";
import { ImageList } from "../../../src/components/design-pages/imageList";

const tileData = [
  {
    src: "https://images.orientbell.com/media/design-images/Green - 6/Elegant Green Bathroom Design with Floral Accents.jpg",
    alt: "Elegant green bathroom design with floral accent wall, modern black vanity, double mirrors, and sleek bathtub.",
  },
  {
    src: "https://images.orientbell.com/media/design-images/Green - 6/Elegant Green Bathroom Design with Patterned Tiles.jpg",
    alt: "Elegant green bathroom design with patterned tiles, floating vanity, dual basins, and modern wall-mounted toilet.",
  },
  {
    src: "https://images.orientbell.com/media/design-images/Green - 6/Green Bathroom Design with Double Vanity and Light.jpg",
    alt: "Green bathroom design with striped wall tiles, large window, and natural lighting.",
  },
  {
    src: "https://images.orientbell.com/media/design-images/Green - 6/Minimalist Green Bathroom Design with Striped Walls.jpg",
    alt: "Minimalist green bathroom design with striped accent wall, wooden floating vanity, vessel sinks, and modern fixtures.",
  },
  {
    src: "https://images.orientbell.com/media/design-images/Green - 6/Modern Green Bathroom Design with Herringbone Tiles.jpg",
    alt: "A modern green bathroom design with herringbone tiles, a floating vanity, a round mirror, and shelves.",
  },
  {
    src: "https://images.orientbell.com/media/design-images/Green - 6/Modern Green Bathroom Design with Stylish Tiles.jpg",
    alt: "Modern green bathroom design with stylish tiles, floating vanity, round mirror, and elegant bathtub setup.",
  },
  {
    src: "https://images.orientbell.com/media/design-images/Green - 6/Ocean Green Bathroom Design with Dolphin Tiles.jpg",
    alt: "Ocean-themed green bathroom design with dolphin wall tiles, modern vanity, glass shower, and stylish decor.",
  },
  {
    src: "https://images.orientbell.com/media/design-images/Green - 6/Stylish Green Bathroom Design with Floral Tiles.jpg",
    alt: "Stylish green bathroom design showcasing vibrant floral tiles, elegant bathtub, cozy seating, and serene ambiance.",
  },
  {
    src: "https://images.orientbell.com/media/design-images/Green - 6/Underwater Green Bathroom Design with Ocean Tiles.jpg",
    alt: "Underwater-themed green bathroom design with ocean-inspired tiles, floating vanity, and dual basins for elegance.",
  },
];

const settings = {
  infinite: true,
  slidesToShow: 4,
  slidesToScroll: 1,
  autoplay: true,
  centerMode: false,
  autoplaySpeed: 3000,
  arrows: true,
  dots: false,
  centerPadding: "0px",
  responsive: [
    {
      breakpoint: 991,
      settings: {
        arrows: true,
        centerMode: false,
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 768,
      settings: {
        arrows: true,
        centerMode: false,
        slidesToShow: 2,
      },
    },
    {
      breakpoint: 641,
      settings: {
        arrows: true,
        centerMode: false,
        slidesToShow: 1,
      },
    },
  ],
};
const faqs = [
  {
    question: "What is the best green bathroom colour?",
    answer:
      "The best green bathroom colour depends on the atmosphere you want to create. Soft options, like sage green, offer a smooth, calming vibe. Luxurious tones, like emerald green, add a sophisticated feel. So, pick a shade that fits your style and desired ambience.",
  },
  {
    question: "What colour tile goes with a green vanity?",
    answer:
      "For a green vanity, neutral-toned tiles work the best. Go for tones like white, grey, or beige to create a balanced and calming effect. Alternatively, you can consider light wood-toned tiles that can complement the green, adding warmth and texture. A soft, muted tile colour enhances the green vanity without overpowering the bathroom.",
  },
  {
    question: "What are the best tile colours for a green bathroom?",
    answer:
      "The best tile colours that will look good in a green bathroom are soft neutrals like white, cream, ivory, and light grey. These tones perfectly balance the green tones. Also, light beige works well, offering warmth and harmony with green.",
  },
  {
    question: "Is green a good colour for a bathroom?",
    answer:
      "Yes, green is an elegant colour for a bathroom. It promotes a calm, relaxing ambience, making it perfect for creating a spa-like retreat. It also helps connect the room to nature, with freshness and tranquillity.",
  },
  {
    question: "How to modernise a green bathroom?",
    answer:
      "To modernise your green bathroom, you can include sleek, minimalistic fixtures like matte black or chrome hardware. Use clean lines and beautiful geometric tile patterns for a contemporary touch. Combine large mirrors and floating vanities to create a more open feel. Also, combining green with neutral tones like white or beige adds a fresh, modern vibe.",
  },
  {
    question: "What goes well with a green bathroom?",
    answer:
      "A green bathroom goes well with neutral shades, like white, beige, or grey. These tones balance the colour and create a serene atmosphere. Also, soft-toned wooden tiles will look good with green tones, adding warmth and a natural touch. Metallic fixtures in chrome complement green, adding a modern, luxurious flair.",
  },
];

const text = `A green bathroom design brings fresh, calm energy to any home. Every green hue can turn any bathroom design into a peaceful retreat. Imagine the calming feeling of green shades on the wall or floor, like stepping into a serene garden. All green bathroom designs make you feel relaxed while establishing a connection to nature. Whether it is a cosy shower or a luxurious tub, the green shades bring warmth and comfort. From wall colours to floor tiles, using matching tones can add beauty to the bathroom design. The right combination of colour and style can turn your bathing space into a true sanctuary. So, here are some green bathroom ideas for homeowners and interior designers to craft for the perfect garden vibes in bathrooms.`;

function GreenBathroom() {
  const router = useRouter();
  const [isExpertTalkClicked, setIsExpertTalkClicked] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [relatedBlogData, setRelatedBlogData] = useState([]);
  const [isReadMore, setIsReadMore] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    getRelatedBlogs();
  }, []);

  const maxChars = 300;
  const previewText = text.substring(0, maxChars);
  const remainingText = text.substring(maxChars);

  const toggleReadMore = () => {
    setIsReadMore(!isReadMore);
  };

  var requestOptions = {
    method: "GET",
  };
  const getRelatedBlogs = async () => {
    // try{

    fetch(
      `${config.HEADLESS_BASE_URL}/blog/wp-json/post/v2/category/term=bathroom-designs`,
      requestOptions
    )
      .then((response) => response.text())
      .then((result) => {
        setRelatedBlogData(JSON.parse(result));
      })
      .catch((error) => console.log("error", error));
  };
  const handleToggle = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const displayedFAQs = showAll ? faqs : faqs.slice(0, 5);
  return (
    <>
      <Head>
        <title>Green Bathroom Ideas | Orientbell</title>
        <meta name="title" content="Green Bathroom Ideas | Orientbell " />
        <meta
          name="description"
          content="Upgrade your space with Green bathroom design ideas from Orientbell. Get inspired and create a serene, stylish bathroom using our versatile Green tile designs"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Design Ideas",
                  item: "/design-ideas/",
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Bathroom Designs",
                  item: "/design-ideas/bathroom-designs",
                },
                {
                  "@type": "ListItem",
                  position: 4,
                  name: "Green Bathroom",
                  item: "/design-ideas/bathroom-designs/green-bathroom",
                },
              ],
            }),
          }}
        ></script>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              url: "/design-ideas/bathroom-designs/green-bathroom",
              headline: "",
              breadcrumb:
                "Home > Design Ideas > Bathroom Designs > Green Bathroom",
              name: "Green Bathroom Ideas | Orientbell",
              description:
                "Upgrade your space with green bathroom design ideas from Orientbell. Get inspired and create a serene, stylish bathroom using our versatile green tile designs",
            }),
          }}
        ></script>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "What aspects should I consider while planning a bathroom?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Consider the available area, the desired overall style, ventilation, lighting, storage needs, and ventilation. Do not forget to infuse your flair into the design as well but give priority to functionality also.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Can a small bathroom be made to appear bigger?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Yes, you can make a small bathroom look bigger with a few smart design choices. Using light colours like white or soft pastels can create an open feel. Large mirrors can reflect light and give an illusion of space. Opt for large tiles with fewer grout lines and install proper lighting. Keeping the décor minimal and using wall-mounted fixtures also helps to make the room feel more spacious.",
                  },
                },
                {
                  "@type": "Question",
                  name: "What are some of the recent trends in bathroom decor?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Popular trends in bathroom décor focus on creating a spa-inspired ambiance with neutral tones, natural elements, and soft lighting. Patterned or textured wall tiles paired with stylish flooring and well-placed mirrors are commonly seen. Additionally, glossy marble tiles for walls combined with matte finish tiles for floors and metallic décor elements, have become popular choices, offering a mix of elegance and contemporary style.",
                  },
                },
                {
                  "@type": "Question",
                  name: "How to select the best flooring for my bathroom?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Select flooring materials that offer resistance against moisture, such as vitrified tiles or ceramic tiles. Orientbell Tiles has a collection of anti-skid tiles that will be ideal for a bathroom. Along with the practical side, consider your design choices, maintenance requirements, and budget.",
                  },
                },

                {
                  "@type": "Question",
                  name: "Which type of lighting works the best in a bathroom?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "You can go ahead with the combination of ambient, and accent lighting to make a bathroom seem cosy and well-lit.",
                  },
                },

                {
                  "@type": "Question",
                  name: "Is it better to work with an expert designer or can I design my own bathroom?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Hiring a designing expert brings experience, saves time, and brings a consistent style. However, if you are on a tight budget, creating your bathroom design is achievable with careful planning and study.",
                  },
                },
              ],
            }),
          }}
        ></script>
      </Head>

      <div className="designpage-section">
        <div className="container">
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href="/design-ideas">Design Ideas</Breadcrumb.Item>
            <Breadcrumb.Item href="/design-ideas/bathroom-designs">
              Bathroom Designs
            </Breadcrumb.Item>
            <Breadcrumb.Item href="/design-ideas/bathroom-designs/green-bathroom">
              Green Bathroom
            </Breadcrumb.Item>
          </Breadcrumb>

          <div className="designpage-heading-bx padding-spacing">
            <h4>Showing {tileData.length} results for </h4>
            <h1>Green Bathroom Design</h1>
            <p className="description-text">
              {previewText}
              {!isReadMore && text.length > maxChars && "..."}
              {isReadMore && <span>{remainingText}</span>}
              {text.length > maxChars && (
                <button onClick={toggleReadMore}>
                  {isReadMore ? "Read Less" : "Read More"}
                </button>
              )}
            </p>
          </div>
          <div className="obl-master-design-section">
            <h2>Green Bathroom Design Gallery</h2>
            <div className="designpage-category-row inner-design-gallery">
              <ImageList tileData={tileData} />
            </div>
            <div className="bottom-content">
              <div className="common-text-content-section">
                <h2>Modern Green Bathroom Ideas</h2>
                <p>
                  Green bathroom ideas bring a fresh, pleasant atmosphere,
                  perfect for spending some alone time. Here are some bathroom
                  ideas that you can craft using green tones.
                </p>
                <ul>
                  <li>
                    <h5>
                      <strong>Fresh and Timeless:</strong>
                    </h5>
                    <p>
                      A green bathroom adds a calm, grounding feeling. Even
                      though it is trendy, it offers a timeless appeal. From
                      sage green to emerald, pick the tone that fits your style.
                    </p>
                  </li>
                  <li>
                    <h5>
                      <strong>Stylish Tiles:</strong>
                    </h5>
                    <p>
                      Green tiles can play a massive role in green bathrooms.
                      Use deep green tiles for drama or soft green tones for a
                      calming touch. Mosaic or marble designs can elevate the
                      look.
                    </p>
                  </li>
                  <li>
                    <h5>
                      <strong>Versatile Materials:</strong>
                    </h5>
                    <p>
                      Consider ceramic and vitrified tiles in green tones for
                      floors and walls. Complement them with matching
                      accessories to enhance the elegance of your space.
                    </p>
                  </li>
                  <li>
                    <h5>
                      <strong>Creative Use of Space:</strong>
                    </h5>
                    <p>
                      Even with space limitations, sleek fixtures and mirrors
                      can make a bathroom feel spacious while keeping the green
                      theme central.
                    </p>
                  </li>
                </ul>

                <h2>How Do I Determine My Modern Green Bathroom Layout?</h2>
                <p>
                  To create the perfect modern green bathroom layout, focus on
                  balance and practicality.
                </p>
                <ul>
                  <li>
                    <h5>
                      <strong>Measure Your Bathroom:</strong>
                    </h5>
                    <p>
                      Start by measuring your space to understand the layout
                      options for essentials like the bathtub, shower, and
                      toilet.
                    </p>
                  </li>
                  <li>
                    <h5>
                      <strong>Know Your Needs:</strong>
                    </h5>
                    <p>
                      Determine essential elements like a vanity or storage
                      cabinet based on your lifestyle and routine.
                    </p>
                  </li>
                  <li>
                    <h5>
                      <strong>Follow Layout Regulations:</strong>
                    </h5>
                    <p>
                      Ensure proper spacing between fixtures for safety and
                      comfort while adhering to standard layout guidelines.
                    </p>
                  </li>
                  <li>
                    <h5>
                      <strong>Tile It Right:</strong>
                    </h5>
                    <p>
                      Choose anti-skid green tiles and pair them with neutral
                      shades like white marble or wooden tiles for contrast.
                    </p>
                  </li>
                </ul>
              </div>

              <div className="common-text-content-section">
             

                <h2>
                  What Materials Should I Use in My Modern Green Bathroom
                  Design?
                </h2>
                <ul>
                  <li>
                    <h5>
                      <strong> Tiles:</strong>
                    </h5>
                    <p>
                      Choose green tiles in matte or glossy finishes for walls
                      and use anti-skid tiles for flooring. Mosaic, floral, and
                      marble-effect tiles add variety.
                    </p>
                  </li>
                  <li>
                    <h5>
                      <strong> Countertops:</strong>
                    </h5>
                    <p>
                      Large granite tiles (800x2400 mm) with neutral shades like
                      white or beige complement green tones beautifully.
                    </p>
                  </li>
                  <li>
                    <h5>
                      <strong>Storage Cabinets:</strong>
                    </h5>
                    <p>
                      Floating vanities in matte or glossy finishes help
                      maintain a sleek look while offering functionality.
                    </p>
                  </li>
                </ul>

                <h2>How Do I Decorate My Green Bathroom?</h2>
                <ul>
                  <li>
                    <h5>
                      <strong> Colour Scheme:</strong>
                    </h5>
                    <p>
                      Pair green with white, grey, or beige for a calm ambiance.
                      Dark and light green shades together can add depth and
                      dimension.
                    </p>
                  </li>
                  <li>
                    <h5>
                      <strong> Play with Tile Designs:</strong>
                    </h5>
                    <p>
                      Use patterned tiles or create a statement wall with bold
                      green tiles for a striking effect.
                    </p>
                  </li>
                  <li>
                    <h5>
                      <strong> Add Greenery:</strong>
                    </h5>
                    <p>
                      Incorporate small potted plants or hanging greenery for a
                      fresh and natural vibe.
                    </p>
                  </li>
                </ul>
              </div>
            </div>{" "}
            <div className="designpage-faq-section padding-spacing">
              <h2>FAQs About Green Bathroom Design</h2>
              <div className="newacd designpage-faq-row">
                <ul className="accordion">
                  {displayedFAQs.map((faq, index) => (
                    <li key={index}>
                      <a className="toggle" onClick={() => handleToggle(index)}>
                        {index + 1}. {faq.question}
                      </a>
                      {activeIndex === index && (
                        <ul className="inner">
                          <li>{faq.answer}</li>
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
                {faqs.length > 5 && (
                  <div className="tru_btn_new design-page">
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowAll(!showAll)}
                    >
                      {showAll ? "Show Less" : "Read More"}
                    </button>
                  </div>
                )}
              </div>
            </div>
            {relatedBlogData?.length > 0 && (
              <div className="tile-related-blog">
                <h2 className="title">Related Blogs</h2>
                <div className="designpage-relatedpost-row">
                  <Slider {...settings}>
                    {relatedBlogData?.map((obj, index) => {
                      return (
                        <div className="related-blog-col" key={index}>
                          <div className="blog-figure">
                            <a href={obj.permalink}>
                              <img
                                src={obj.image}
                                loading="lazy"
                                alt="Marble Tiles Vs Marble Slab: What is Better for Your Home?"
                              />
                            </a>
                          </div>
                          <div className="blog-text">
                            <div className="listingDate">{obj.date}</div>
                            <div className="listingHead">
                              <a href={obj.permalink}>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: obj.title,
                                  }}
                                />
                              </a>
                            </div>
                            <div className="listingText">
                              <p>
                                {" "}
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: obj.content.slice(0, -11),
                                  }}
                                />
                              </p>
                              <div className="listingButton">
                                <a href={obj.permalink}>Read More</a>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </Slider>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default GreenBathroom;
